import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2, Database, GitBranch, Layers3, ShieldCheck, Sparkles, Terminal, Workflow, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { LiveProductPreview } from "@/components/landing/LiveProductPreview";

type CardProps = {
  index: number;
  label: string;
  title: string;
  description: string;
  children: React.ReactNode;
  onEnter: (index: number) => void;
};

function ShowcaseCard({ index, label, title, description, children, onEnter }: CardProps) {
  const ref = useRef<HTMLElement | null>(null);
  const inView = useInView(ref, { once: true, margin: "-18% 0px -18% 0px" });
  const reducedMotion = useReducedMotion();

  return (
    <motion.article
      ref={ref}
      onViewportEnter={() => onEnter(index)}
      initial={reducedMotion ? false : { opacity: 0, y: 42 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reducedMotion ? 0 : 0.65, ease: "easeOut" }}
      className="relative overflow-hidden rounded-[1.75rem] border border-border/50 bg-card/45 shadow-[0_28px_90px_-55px_hsl(var(--primary)/0.55)] backdrop-blur-xl"
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_80%_0%,hsl(255_60%_55%/0.08),transparent_50%)]" />
      <div className="relative grid lg:grid-cols-[0.78fr_1.22fr]">
        <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-primary/30 bg-primary/10 px-1.5 text-[9px]">
              {String(index + 1).padStart(2, "0")}
            </span>
            {label}
          </div>
          <h3 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">{title}</h3>
          <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground">{description}</p>
        </div>
        <div className="min-w-0 border-t lg:border-t-0 lg:border-l border-border/40 bg-background/35 p-4 sm:p-6">{children}</div>
      </div>
    </motion.article>
  );
}

function ArchitectureCanvas() {
  const nodes = [
    { icon: Layers3, label: "Frontend", value: "Pages + components" },
    { icon: Workflow, label: "API", value: "Routes + services" },
    { icon: Database, label: "Data", value: "Schema + relationships" },
    { icon: ShieldCheck, label: "Auth", value: "Roles + permissions" },
  ];

  return (
    <div className="rounded-2xl border border-border/50 bg-background/65 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2"><Workflow className="h-4 w-4 text-primary" /><span className="text-xs font-semibold">System blueprint</span></div>
        <span className="rounded-full bg-emerald-400/10 px-2 py-1 text-[9px] font-semibold text-emerald-400">RESOLVED</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {nodes.map((node, index) => (
          <motion.div key={node.label} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.08, duration: 0.35 }} className="rounded-xl border border-border/45 bg-card/55 p-3">
            <node.icon className="h-4 w-4 text-primary mb-2" />
            <p className="text-xs font-semibold">{node.label}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">{node.value}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-center gap-2 text-[9px] text-muted-foreground"><span className="h-px flex-1 bg-border/50" /><span>structured before execution</span><span className="h-px flex-1 bg-border/50" /></div>
    </div>
  );
}

function BuildCanvas() {
  const stages = [
    { name: "Frontend", detail: "components + routes", progress: 100 },
    { name: "Backend", detail: "services + APIs", progress: 100 },
    { name: "Database", detail: "schema + policies", progress: 100 },
    { name: "Security", detail: "auth + validation", progress: 92 },
  ];

  return (
    <div className="rounded-2xl border border-border/50 bg-background/65 overflow-hidden">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <div className="flex items-center gap-2"><Terminal className="h-4 w-4 text-primary" /><span className="text-xs font-semibold">Build execution</span></div>
        <span className="inline-flex items-center gap-1.5 text-[9px] text-cyan-400"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />RUNNING</span>
      </div>
      <div className="p-4 space-y-3">
        {stages.map((stage, index) => (
          <div key={stage.name}>
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" /><span className="text-[11px] font-medium">{stage.name}</span><span className="hidden sm:inline text-[9px] text-muted-foreground truncate">{stage.detail}</span></div>
              <span className="text-[9px] font-mono text-muted-foreground">{stage.progress}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted/70">
              <motion.div initial={{ width: 0 }} whileInView={{ width: stage.progress + "%" }} viewport={{ once: true }} transition={{ duration: 0.8, delay: index * 0.12, ease: "easeOut" }} className="h-full rounded-full bg-gradient-to-r from-primary to-cyan-400" />
            </div>
          </div>
        ))}
        <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5 text-[10px] text-muted-foreground"><span className="font-semibold text-foreground">Execution is staged.</span> Each layer advances after its checks complete.</div>
      </div>
    </div>
  );
}

function ValidationCanvas() {
  const checks = [
    { label: "Type checks", value: "passed" },
    { label: "Route checks", value: "passed" },
    { label: "Data policies", value: "passed" },
    { label: "Runtime probe", value: "repaired" },
  ];

  return (
    <div className="rounded-2xl border border-border/50 bg-background/65 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /><span className="text-xs font-semibold">Verification loop</span></div><span className="text-[9px] font-semibold text-emerald-400">HEALTHY</span></div>
      <div className="space-y-2">
        {checks.map((check, index) => (
          <motion.div key={check.label} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1, duration: 0.35 }} className="flex items-center justify-between gap-3 rounded-xl border border-border/40 bg-card/45 px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">{check.value === "repaired" ? <Wrench className="h-3.5 w-3.5 text-amber-400 shrink-0" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}<span className="text-[11px]">{check.label}</span></div>
            <span className={"text-[9px] font-semibold uppercase " + (check.value === "repaired" ? "text-amber-400" : "text-emerald-400")}>{check.value}</span>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-2.5"><GitBranch className="h-3.5 w-3.5 text-amber-400 shrink-0" /><div className="text-[10px] leading-relaxed text-muted-foreground"><span className="font-semibold text-foreground">Targeted repair</span> closes the loop before the next stage proceeds.</div></div>
    </div>
  );
}

function ProductionCanvas() {
  return (
    <div className="rounded-2xl border border-border/50 bg-background/65 overflow-hidden shadow-[0_20px_60px_-45px_hsl(var(--primary)/0.8)]">
      <div className="flex items-center justify-between gap-3 border-b border-border/40 bg-muted/20 px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Layers3 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate text-[11px] font-medium text-foreground">Application preview</span>
        </div>
        <span className="shrink-0 rounded-full border border-border/60 bg-background/60 px-2 py-1 text-[9px] font-medium tracking-wide text-muted-foreground">SHOWCASE</span>
      </div>
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-[0.8fr_1.2fr] gap-4">
          <div className="space-y-2"><div className="h-3 w-20 rounded bg-primary/20" /><div className="h-2 w-full rounded bg-muted/70" /><div className="h-2 w-5/6 rounded bg-muted/70" /><div className="mt-5 h-16 rounded-xl border border-border/40 bg-card/60" /><div className="h-16 rounded-xl border border-border/40 bg-card/60" /></div>
          <div className="rounded-2xl border border-border/40 bg-card/55 p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3"><div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center"><Sparkles className="h-3.5 w-3.5 text-primary" /></div><div><div className="h-2.5 w-20 rounded bg-foreground/20" /><div className="mt-1.5 h-2 w-28 rounded bg-muted/70" /></div></div>
            <div className="space-y-2"><div className="h-2 rounded bg-muted/70" /><div className="h-2 w-11/12 rounded bg-muted/70" /><div className="h-2 w-4/5 rounded bg-muted/70" /></div>
            <div className="mt-5 h-8 rounded-lg bg-primary/15 border border-primary/20" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function NuvraBuildShowcase() {
  const { t } = useI18n();
  const [activeStep, setActiveStep] = useState(0);
  const reducedMotion = useReducedMotion();
  const steps = [
    { label: t("landing.showcase.step1.label"), title: t("landing.showcase.step1.title") },
    { label: t("landing.showcase.step2.label"), title: t("landing.showcase.step2.title") },
    { label: t("landing.showcase.step3.label"), title: t("landing.showcase.step3.title") },
    { label: t("landing.showcase.step4.label"), title: t("landing.showcase.step4.title") },
    { label: t("landing.showcase.step5.label"), title: t("landing.showcase.step5.title") },
  ];

  return (
    <section id="build-showcase" className="relative overflow-hidden border-y border-border/20 py-14 sm:py-24">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_0%,hsl(255_65%_55%/0.09),transparent_52%)]" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-[0.34fr_0.66fr] gap-8 lg:gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <motion.div initial={reducedMotion ? false : { opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-primary">{t("landing.showcase.eyebrow")}</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">{t("landing.showcase.h2a")} <span className="bg-gradient-to-r from-primary via-violet-400 to-nuvra-accent bg-clip-text text-transparent">{t("landing.showcase.h2b")}</span></h2>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-muted-foreground">{t("landing.showcase.desc")}</p>
              <div className="mt-7 hidden lg:block">
                <div className="relative pl-6">
                  <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border/60" />
                  <motion.div className="absolute left-[4px] top-2 w-[3px] rounded-full bg-primary" animate={{ height: Math.max(10, (activeStep + 1) * 20) + "%" }} transition={{ duration: reducedMotion ? 0 : 0.35 }} />
                  <div className="space-y-5">
                    {steps.map((step, index) => (
                      <div key={step.label} className="relative flex items-start gap-3">
                        <span className={"absolute -left-[27px] top-0.5 h-3 w-3 rounded-full border " + (activeStep === index ? "border-primary bg-primary shadow-[0_0_14px_hsl(var(--primary)/0.7)]" : "border-border bg-background")} />
                        <div><p className={"text-[10px] font-semibold uppercase tracking-wider " + (activeStep === index ? "text-primary" : "text-muted-foreground")}>{step.label}</p><p className="mt-0.5 text-xs text-foreground">{step.title}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Link to="/signup" className="group mt-7 inline-flex items-center gap-2 rounded-xl border border-border/60 bg-card/50 px-4 py-2.5 text-xs font-semibold transition-all hover:border-primary/50 hover:bg-primary/5">
                {t("landing.showcase.cta")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>

          <div className="space-y-6">
            <ShowcaseCard index={0} label={t("landing.showcase.step1.label")} title={t("landing.showcase.step1.title")} description={t("landing.showcase.step1.desc")} onEnter={setActiveStep}><LiveProductPreview compact /></ShowcaseCard>
            <ShowcaseCard index={1} label={t("landing.showcase.step2.label")} title={t("landing.showcase.step2.title")} description={t("landing.showcase.step2.desc")} onEnter={setActiveStep}><ArchitectureCanvas /></ShowcaseCard>
            <ShowcaseCard index={2} label={t("landing.showcase.step3.label")} title={t("landing.showcase.step3.title")} description={t("landing.showcase.step3.desc")} onEnter={setActiveStep}><BuildCanvas /></ShowcaseCard>
            <ShowcaseCard index={3} label={t("landing.showcase.step4.label")} title={t("landing.showcase.step4.title")} description={t("landing.showcase.step4.desc")} onEnter={setActiveStep}><ValidationCanvas /></ShowcaseCard>
            <ShowcaseCard index={4} label={t("landing.showcase.step5.label")} title={t("landing.showcase.step5.title")} description={t("landing.showcase.step5.desc")} onEnter={setActiveStep}><ProductionCanvas /></ShowcaseCard>
          </div>
        </div>
      </div>
    </section>
  );
}
